import {
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ReportIcon from '@mui/icons-material/Report';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import CodeIcon from '@mui/icons-material/Code';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import { alpha } from '@mui/material/styles';
import Visibility from '../../../common/components/Visibility.tsx';
import { NavigationRoutes } from '../../router/routes.ts';
import { SideBarMenuProps } from './SideBarMenuProps.ts';
import { Assignment } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const SideBarCustomerAdmin = ({ handleMenuClick }: SideBarMenuProps) => {
    const [openMenu, setOpenMenu] = useState<{ [key: string]: boolean }>({
        admin: true, // Keep Administration menu open by default
    });
    const location = useLocation();
    const pathname = location.pathname;

    const isTransactionActive = pathname.startsWith(
        NavigationRoutes.transaction
    );
    const isReportsActive = pathname.startsWith(NavigationRoutes.reports);
    const isDeviationsActive = pathname.startsWith(NavigationRoutes.deviations);

    const isAdminGroupActive = [
        NavigationRoutes.userManagement,
        NavigationRoutes.drugManagement,
        NavigationRoutes.storageManagement,
        NavigationRoutes.labelPrinting,
    ].some((base) => pathname.startsWith(base));
    const isUsersActive = pathname.startsWith(NavigationRoutes.userManagement);
    const isDrugsActive = pathname.startsWith(NavigationRoutes.drugManagement);
    const isStorageActive = pathname.startsWith(
        NavigationRoutes.storageManagement
    );
    const isLabelsActive = pathname.startsWith(NavigationRoutes.labelPrinting);
    const isMessagesActive = pathname.startsWith(NavigationRoutes.messages);
    const isProfileActive = pathname.startsWith(NavigationRoutes.profile);
    const isSupportActive = pathname.startsWith(NavigationRoutes.support);

    const handleToggle = (menu: string) => {
        setOpenMenu((prev) => ({ ...prev, [menu]: !prev[menu] }));
    };

    return (
        <nav>
            <List>
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isTransactionActive}
                    onClick={() =>
                        handleMenuClick(NavigationRoutes.transaction)
                    }
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <MenuBookIcon />
                    </ListItemIcon>
                    <ListItemText primary="Förbrukningsjournal" />
                </ListItemButton>
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isReportsActive}
                    onClick={() => handleMenuClick(NavigationRoutes.reports)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <Assignment />
                    </ListItemIcon>
                    <ListItemText primary="Rapporter" />
                </ListItemButton>
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isDeviationsActive}
                    onClick={() => handleMenuClick(NavigationRoutes.deviations)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <ReportIcon />
                    </ListItemIcon>
                    <ListItemText primary="Avvikelser" />
                </ListItemButton>

                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isMessagesActive}
                    onClick={() => handleMenuClick(NavigationRoutes.messages)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <MailOutlineIcon />
                    </ListItemIcon>
                    <ListItemText primary="Meddelanden" />
                </ListItemButton>

                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isAdminGroupActive}
                    onClick={() => handleToggle('admin')}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Administration" />
                    {openMenu['admin'] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openMenu['admin']} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mt: 0.5 }}>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                                mb: 0.5,
                            })}
                            selected={isUsersActive}
                            onClick={() =>
                                handleMenuClick(NavigationRoutes.userManagement)
                            }
                        >
                            <ListItemText
                                inset
                                primary="Användare"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isUsersActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isUsersActive ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                                mb: 0.5,
                            })}
                            selected={isDrugsActive}
                            onClick={() =>
                                handleMenuClick(NavigationRoutes.drugManagement)
                            }
                        >
                            <ListItemText
                                inset
                                primary="Läkemedel"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isDrugsActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isDrugsActive ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                                mb: 0.5,
                            })}
                            selected={isStorageActive}
                            onClick={() =>
                                handleMenuClick(
                                    NavigationRoutes.storageManagement
                                )
                            }
                        >
                            <ListItemText
                                inset
                                primary="Lagerställe"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isStorageActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isStorageActive ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                            })}
                            selected={isLabelsActive}
                            onClick={() =>
                                handleMenuClick(NavigationRoutes.labelPrinting)
                            }
                        >
                            <ListItemText
                                inset
                                primary="Skriv ut QR-koder"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isLabelsActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isLabelsActive ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                    </List>
                </Collapse>

                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isSupportActive}
                    onClick={() => handleMenuClick(NavigationRoutes.support)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <ContactSupportIcon />
                    </ListItemIcon>
                    <ListItemText primary="Support" />
                </ListItemButton>

                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isProfileActive}
                    onClick={() => handleMenuClick(NavigationRoutes.profile)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <PersonIcon />
                    </ListItemIcon>
                    <ListItemText primary="Min profil" />
                </ListItemButton>

                <Visibility visible={import.meta.env.MODE === 'development'}>
                    <ListItemButton
                        sx={{ my: 0.5 }}
                        onClick={() => handleToggle('dev')}
                    >
                        <ListItemIcon sx={{ minWidth: 34 }}>
                            <CodeIcon />
                        </ListItemIcon>
                        <ListItemText primary="Development" />
                        {openMenu['dev'] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={openMenu['dev']} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ mt: 0.5 }}>
                            <ListItemButton
                                sx={(theme) => ({
                                    ml: 2,
                                    pl: 1,
                                    py: 0.5,
                                    minHeight: 36,
                                    borderRadius: 1,
                                    '&.Mui-selected': {
                                        borderLeft: '3px solid',
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.08
                                        ),
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(
                                            theme.palette.action.hover,
                                            0.04
                                        ),
                                    },
                                    mb: 0.5,
                                })}
                                onClick={() =>
                                    handleMenuClick(NavigationRoutes.devTheme)
                                }
                            >
                                <ListItemText
                                    inset
                                    primary="Themes-page"
                                    slotProps={{
                                        primary: {
                                            fontSize: '0.875rem',
                                            color: 'text.secondary',
                                            fontWeight: 400,
                                        },
                                    }}
                                />
                            </ListItemButton>
                            <ListItemButton
                                sx={(theme) => ({
                                    ml: 2,
                                    pl: 1,
                                    py: 0.5,
                                    minHeight: 36,
                                    borderRadius: 1,
                                    '&.Mui-selected': {
                                        borderLeft: '3px solid',
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.08
                                        ),
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(
                                            theme.palette.action.hover,
                                            0.04
                                        ),
                                    },
                                })}
                                onClick={() =>
                                    handleMenuClick(NavigationRoutes.devMessage)
                                }
                            >
                                <ListItemText
                                    inset
                                    primary="Message page"
                                    slotProps={{
                                        primary: {
                                            fontSize: '0.875rem',
                                            color: 'text.secondary',
                                            fontWeight: 400,
                                        },
                                    }}
                                />
                            </ListItemButton>
                        </List>
                    </Collapse>
                </Visibility>
            </List>
        </nav>
    );
};

export default SideBarCustomerAdmin;
